import {
  RichTextEditor,
  Link,
  useRichTextEditorContext,
} from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import { useEffect } from "react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import PlaceholderMenu from "./PlaceholderMenu";
import ButtonControl from "./ButtonControl";
import LinkControl from "../Pages/LinkControl";
import { withButtonAttrs } from "./emailButton";
import { IconQuote } from "@tabler/icons-react";

// Keep Mantine's Link (its Mod-k shortcut / openOnClick config) but also
// preserve the inline style + button-marker attributes so an inserted button
// <a> survives ProseMirror serialization.
const CampaignLink = withButtonAttrs(Link);

function PlaceholderControl() {
  const { editor } = useRichTextEditorContext();

  return (
    <PlaceholderMenu
      onInsert={(value) => editor?.commands.insertContent(value)}
      includeWorkspace
      menuIcon={
        <RichTextEditor.Control
          aria-label="Insert placeholder"
          title="Insert placeholder"
        >
          <IconQuote size={16} stroke={1.5} />
        </RichTextEditor.Control>
      }
    />
  );
}

const CampaignEditor = ({
  initialContent,
  onChange,
  accentColor,
  // Programmatic content + a version counter. Bumping the version (e.g. when a
  // template is applied) replaces the editor's content; the version key avoids
  // re-setting on every render and clobbering the admin's own edits.
  appliedContent,
  appliedVersion = 0,
  // When true, the button control offers a one-click "event register button"
  // whose href is the {{registerLink}} placeholder (used by invite emails).
  registerLinkPreset = false,
}) => {
  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({ link: false }),
      TextAlign.configure({ types: ["paragraph", "heading"] }),
      CampaignLink,
    ],
    content: initialContent || "",
    parseOptions: {
      preserveWhitespace: "full",
    },
    onUpdate: ({ editor }) =>
      onChange({ html: editor.getHTML(), json: editor.getJSON() }),
    editorProps: {
      attributes: { style: "min-height: 250px; cursor: text" },
    },
  });

  useEffect(() => {
    if (!editor || appliedVersion === 0) return;
    // emitUpdate: true so onChange fires and the form/preview pick up the
    // template content (subject/attachments are handled by the caller).
    editor.commands.setContent(appliedContent || "", true, {
      preserveWhitespace: "full",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedVersion, editor]);

  return (
    <RichTextEditor editor={editor}>
      <RichTextEditor.Toolbar>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
          <RichTextEditor.ClearFormatting />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.H1 />
          <RichTextEditor.H2 />
          <RichTextEditor.H3 />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.AlignLeft />
          <RichTextEditor.AlignCenter />
          <RichTextEditor.AlignRight />
          <RichTextEditor.AlignJustify />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <LinkControl editor={editor} />
          <RichTextEditor.Unlink />
          <ButtonControl
            accentColor={accentColor}
            registerLinkPreset={registerLinkPreset}
          />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <PlaceholderControl />
        </RichTextEditor.ControlsGroup>
      </RichTextEditor.Toolbar>

      <RichTextEditor.Content />
    </RichTextEditor>
  );
};

export default CampaignEditor;
